import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader/PageHeader';
import DefaultCard from '../../components/default/DefaultCard';
import InputField from '../../components/InputField';
import SelectTwo from '../../components/SelectTwo';
import OptionSelector from '../../components/OptionSelector';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import PDFSignatureEditor from '../../components/PDFSignatureEditor';
import useHandleErrorFields from '../../hooks/useHandleErrorFields';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import useRut from '../../hooks/useRut';
import useInapiApplicationService from '../../app/services/hooks/useInapiApplicationService';
import useLocationService, { Country, Region, Commune } from '../../app/services/hooks/useLocationService';
import { buildEndpoint } from '../../app/services/EndPoints';
import { AuthContext } from '../../contexts/AuthContext';
import '../../styles/page-components.css';

interface InapiApplicationData {
    verification_id: number;
    person_type: string;
    
    // Datos del Representante Legal
    legal_representative_first_name: string;
    legal_representative_maternal_last_name: string;
    legal_representative_paternal_last_name: string;
    legal_representative_rut: string;
    legal_representative_email: string;
    legal_representative_phone: string;
    legal_representative_nationality_country: string;
    
    // Datos de la Persona Jurídica (solo si person_type === 'juridica')
    company_rut: string;
    company_name: string;
    
    // Dirección
    residence_country_id: number | null;
    residence_country_name: string;
    residence_region_id: number | null;
    residence_region_name: string;
    residence_commune_id: number | null;
    residence_commune_name: string;
    residence_city: string; // Ciudad manual (para países diferentes a Chile)
    residence_address: string;
    
    // Datos del Poder Simple (11 campos según el formulario)
    power_city: string;              // 1. Ciudad
    power_day: number;               // 2. Día
    power_month: number;             // 3. Mes
    power_year: number;              // 4. Año
    legal_representative_full_name: string;  // 5. Nombre completo
    // Nota: legal_representative_rut, company_name y company_rut se reutilizan
    power_address: string;           // 9. Domicilio
    power_municipality: string;      // 10. Comuna
    power_residence_city: string;    // 11. Ciudad de residencia
    
    // Archivos
    signature_image: File | null;
    signature_image_url: string;
    power_document_url: string;
}

const InapiApplication: React.FC = () => {
    const history = useHistory();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    const { requestConfirmation } = useReactConfirmAlert();
    const { formatRut, validateRut } = useRut();
    const { getCountries, getRegions, getCommunesByRegion, fetchingCountries, fetchingRegions, fetchingCommunes } = useLocationService();
    const {
        fetchingCreateApplication,
        fetchingUpdatePowerAndSignature,
        fetchingConfirmApplication,
        fetchingGetApplication,
        fetchingGeneratePowerPdf,
        fetchingSaveSignedPdf,
        createInapiApplication,
        updatePowerAndSignature,
        confirmInapiApplication,
        downloadPowerDocument,
        getInapiApplication,
        generatePowerPdf,
        saveSignedPdf
    } = useInapiApplicationService();
    
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [showPdfSignatureEditor, setShowPdfSignatureEditor] = useState(false);
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
    
    // Datos del formulario
    const [formData, setFormData] = useState<InapiApplicationData>({
        verification_id: 0,
        person_type: 'juridica',
        
        // Datos del Representante Legal
        legal_representative_first_name: '',
        legal_representative_maternal_last_name: '',
        legal_representative_paternal_last_name: '',
        legal_representative_rut: '',
        legal_representative_email: '',
        legal_representative_phone: '',
        legal_representative_nationality_country: 'Chile',
        
        // Datos de la Persona Jurídica
        company_rut: '',
        company_name: '',
        
        // Dirección
        residence_country_id: null,
        residence_country_name: 'Chile',
        residence_region_id: null,
        residence_region_name: '',
        residence_commune_id: null,
        residence_commune_name: '',
        residence_city: '',
        residence_address: '',
        
        // Datos del Poder Simple (11 campos)
        power_city: '',
        power_day: new Date().getDate(),
        power_month: new Date().getMonth() + 1,
        power_year: new Date().getFullYear(),
        legal_representative_full_name: '',
        power_address: '',
        power_municipality: '',
        power_residence_city: '',
        
        // Archivos
        signature_image: null,
        signature_image_url: '',
        power_document_url: ''
    });
    
    // Estados para ubicaciones
    const [countries, setCountries] = useState<Country[]>([]);
    const [regions, setRegions] = useState<Region[]>([]);
    const [communes, setCommunes] = useState<Commune[]>([]);
    
    // Datos del solicitante original (para pre-rellenar en persona natural)
    const { auth } = useContext(AuthContext);

    const [applicantData, setApplicantData] = useState<{
        first_name: string;
        paternal_last_name: string;
        maternal_last_name: string;
        email: string;
        phone: string;
    } | null>(null);

    // El abogado también llena esta solicitud. En ese caso no firma él: prepara el
    // documento y le envía el enlace al cliente desde la etapa 3.
    const applicantEmail = applicantData?.email || formData.legal_representative_email;
    const isApplicant = Boolean(
        auth?.email && applicantEmail &&
        auth.email.toLowerCase() === applicantEmail.toLowerCase()
    );

    const steps = [
        'Datos del solicitante',
        'Poder simple y firma',
        'Resumen y confirmación'
    ];
    
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const verificationId = params.get('verification_id');
        const initialStep = params.get('step');
        
        if (verificationId) {
            setFormData(prev => ({ ...prev, verification_id: parseInt(verificationId) }));
            loadExistingApplication(parseInt(verificationId), initialStep ? parseInt(initialStep) : undefined);
        }
        
        loadCountries();
        loadRegions();
    }, []);
    
    // Cargar comunas cuando cambie la región seleccionada
    useEffect(() => {
        if (formData.residence_region_id) {
            loadCommunesByRegion(formData.residence_region_id);
        } else {
            setCommunes([]);
        }
    }, [formData.residence_region_id]);
    
    const loadCountries = () => {
        getCountries({
            onSuccess: (response: any) => {
                if (response.data?.countries) {
                    setCountries(response.data.countries);
                    // Seleccionar Chile por defecto si no hay país seleccionado
                    if (response.data.default_country_id) {
                        const defaultCountry = response.data.countries.find(
                            (c: Country) => c.id === response.data.default_country_id
                        );
                        if (defaultCountry) {
                            // Se mira el estado actual (no el de este render): si la
                            // solicitud guardada ya trajo un país, no se reemplaza
                            setFormData(prev => prev.residence_country_id ? prev : {
                                ...prev,
                                residence_country_id: defaultCountry.id,
                                residence_country_name: defaultCountry.name
                            });
                        }
                    }
                }
            },
            onError: (response: any) => {
                toast.error('Error al cargar los países');
            }
        });
    };
    
    const loadRegions = () => {
        getRegions({
            onSuccess: (response: any) => {
                if (response.data?.regions) {
                    setRegions(response.data.regions);
                }
            },
            onError: (response: any) => {
                toast.error('Error al cargar las regiones');
            }
        });
    };
    
    const loadCommunesByRegion = (regionId: number) => {
        getCommunesByRegion(regionId, {
            onSuccess: (response: any) => {
                if (response.data?.communes) {
                    setCommunes(response.data.communes);
                }
            },
            onError: (response: any) => {
                toast.error('Error al cargar las comunas');
            }
        });
    };
    
    const loadExistingApplication = (verificationId: number, initialStep?: number) => {
        getInapiApplication(verificationId, {
            onSuccess: (response: any) => {
                if (response.data?.applicant_data) {
                    setApplicantData(response.data.applicant_data);
                }
                
                if (response.data && response.data.inapi_application) {
                    const app = response.data.inapi_application;
                    console.log('Datos de aplicación INAPI cargados:', app);
                    
                    const fullName = `${app.legal_representative_first_name || ''} ${app.legal_representative_paternal_last_name || ''} ${app.legal_representative_maternal_last_name || ''}`.trim();
                    
                    // Si viene un paso inicial desde la URL, navegar a ese paso
                    if (initialStep !== undefined && initialStep >= 0 && initialStep <= 2) {
                        setCurrentStep(initialStep);
                    }
                    
                    setFormData({
                        verification_id: verificationId,
                        person_type: app.person_type || 'juridica',
                        
                        // Datos del Representante Legal
                        legal_representative_first_name: app.legal_representative_first_name || '',
                        legal_representative_paternal_last_name: app.legal_representative_paternal_last_name || '',
                        legal_representative_maternal_last_name: app.legal_representative_maternal_last_name || '',
                        legal_representative_rut: app.legal_representative_rut || '',
                        legal_representative_email: app.email || '',
                        legal_representative_phone: app.phone || '',
                        legal_representative_nationality_country: app.nationality_country || '',
                        
                        // Datos de la Persona Jurídica
                        company_name: app.company_name || '',
                        company_rut: app.company_rut || '',
                        
                        // Dirección
                        residence_country_id: app.residence_country_id || null,
                        residence_country_name: app.residence_country_name || 'Chile',
                        residence_region_id: app.residence_region_id || null,
                        residence_region_name: app.residence_region_name || '',
                        residence_commune_id: app.residence_commune_id || null,
                        residence_commune_name: app.residence_commune_name || '',
                        residence_city: app.residence_city || '',
                        residence_address: app.address || '',
                        
                        // Datos del Poder Simple (pre-rellenar con datos del solicitante si están vacíos)
                        power_city: app.power_city || app.residence_city || app.residence_commune_name || '',
                        power_day: app.power_day || new Date().getDate(),
                        power_month: app.power_month || new Date().getMonth() + 1,
                        power_year: app.power_year || new Date().getFullYear(),
                        legal_representative_full_name: app.legal_representative_full_name || fullName,
                        power_address: app.power_address || app.address || '',
                        power_municipality: app.power_municipality || app.residence_commune_name || app.residence_city || '',
                        power_residence_city: app.power_residence_city || app.residence_city || app.residence_commune_name || '',
                        
                        // Archivos
                        signature_image: null,
                        signature_image_url: app.signature_image_url || '',
                        power_document_url: app.power_document_url || ''
                    });
                    
                    toast.success('Datos cargados correctamente');
                } else {
                    // No hay aplicación INAPI aún, pero tenemos datos del solicitante
                    console.log('No hay aplicación INAPI, datos del solicitante disponibles:', response.data?.applicant_data);
                }
            },
            onError: (error: any) => {
                // Si no existe aplicación INAPI, no es un error crítico
                console.log('No se encontró aplicación INAPI existente, iniciando nueva');
            }
        });
    };
    
    const handleInputChange = (name: string) => (value: string) => {
        setFormData({ ...formData, [name]: value });
    };
    
    // Handler especial para residence_city que también actualiza campos del poder
    const handleResidenceCityChange = (value: string) => {
        setFormData({ 
            ...formData, 
            residence_city: value,
            // Auto-completar la ciudad del poder (la comuna sale del selector de comuna)
            power_residence_city: value
        });
    };
    
    const handleRutChange = (name: string) => (value: string) => {
        // Formatear el RUT mientras se escribe
        const formattedRut = formatRut(value);
        setFormData({ ...formData, [name]: formattedRut });
    };
    
    // Helper para verificar si el país seleccionado es Chile
    const isChileSelected = (): boolean => {
        const selectedCountry = countries.find(c => c.id === formData.residence_country_id);
        // Verificar por código o por nombre
        return selectedCountry?.code === 'CL' || 
               selectedCountry?.name?.toLowerCase() === 'chile' ||
               formData.residence_country_name?.toLowerCase() === 'chile';
    };
    
    const handleCountryChange = (option: any) => {
        // El select puede volver a notificar el mismo país (al montar o al recargar
        // opciones): solo se limpian región, comuna y ciudad si el país cambió de verdad.
        const nextCountryId = option ? option.value : null;
        if ((nextCountryId ?? null) === (formData.residence_country_id ?? null)) {
            return;
        }
        if (option) {
            const selectedCountry = countries.find(c => c.id === option.value);
            
            setFormData({ 
                ...formData, 
                residence_country_id: option.value,
                residence_country_name: selectedCountry?.name || '',
                // Limpiar región, comuna y ciudad al cambiar país
                residence_region_id: null,
                residence_region_name: '',
                residence_commune_id: null,
                residence_commune_name: '',
                residence_city: ''
            });
        } else {
            setFormData({ 
                ...formData, 
                residence_country_id: null,
                residence_country_name: '',
                residence_region_id: null,
                residence_region_name: '',
                residence_commune_id: null,
                residence_commune_name: '',
                residence_city: ''
            });
        }
    };
    
    const handleRegionChange = (option: any) => {
        if (option) {
            const selectedRegion = regions.find(r => r.id === option.value);
            setFormData({ 
                ...formData, 
                residence_region_id: option.value,
                residence_region_name: selectedRegion?.name || '',
                residence_commune_id: null,
                residence_commune_name: ''
            });
        } else {
            setFormData({ 
                ...formData, 
                residence_region_id: null,
                residence_region_name: '',
                residence_commune_id: null,
                residence_commune_name: ''
            });
        }
    };
    
    const handleCommuneChange = (option: any) => {
        if (option) {
            const selectedCommune = communes.find(c => c.id === option.value);
            setFormData({ 
                ...formData, 
                residence_commune_id: option.value,
                residence_commune_name: selectedCommune?.name || '',
                // Auto-completar la comuna del poder (la ciudad sale del campo Ciudad)
                power_municipality: selectedCommune?.name || ''
            });
        } else {
            setFormData({ 
                ...formData, 
                residence_commune_id: null,
                residence_commune_name: '',
                power_municipality: ''
            });
        }
    };
    
    const handleNumberChange = (name: string) => (value: string) => {
        const numValue = parseInt(value) || 0;
        setFormData({ ...formData, [name]: numValue });
    };
    
    const handleGeneratePower = async () => {
        try {
            // Crear FormData con los datos del poder (11 campos)
            const powerData = new FormData();
            powerData.append('verification_id', formData.verification_id.toString());
            
            // Campos del poder
            powerData.append('power_city', formData.power_city);  // 1. Ciudad
            powerData.append('power_day', formData.power_day.toString());  // 2. Día
            powerData.append('power_month', formData.power_month.toString());  // 3. Mes
            powerData.append('power_year', formData.power_year.toString());  // 4. Año
            powerData.append('legal_representative_full_name', formData.legal_representative_full_name);  // 5. Nombre completo
            powerData.append('legal_representative_rut', formData.legal_representative_rut);  // 6. N° Rut
            powerData.append('company_name', formData.company_name);  // 7. Razón social
            powerData.append('company_rut', formData.company_rut);  // 8. N° Rut empresa
            powerData.append('power_address', formData.power_address);  // 9. Domicilio
            powerData.append('power_municipality', formData.power_municipality);  // 10. Comuna
            powerData.append('power_residence_city', formData.power_residence_city);  // 11. Ciudad residencia
            
            const response = await fetch(
                buildEndpoint('brand-registrations/inapi-application/generate-power-document'),
                {
                    method: 'POST',
                    body: powerData
                }
            );
            
            const result = await response.json();
            
            if (response.ok && result.success) {
                // El documento se guardó automáticamente, solo guardamos la URL
                setFormData({ ...formData, power_document_url: result.url });
                toast.success('Documento de poder generado correctamente');
            } else {
                toast.error('Error al generar el documento');
                throw new Error('Error generando poder');
            }
        } catch (error) {
            console.error('Error generando poder:', error);
            toast.error('Error al generar el documento');
            throw error; // Re-lanzar para que handleNext lo capture
        }
    };
    
    const handleDownloadFinalDocument = () => {
        if (formData.verification_id) {
            toast.info('Descargando documento...');
            downloadPowerDocument(formData.verification_id).catch((e: Error) => toast.error(e.message));
            } else {
            toast.error('No se pudo obtener el ID de verificación');
        }
    };
    
    /**
     * Arma el payload del poder simple a partir del formulario.
     * Lo usan tanto la firma del solicitante como la generación que hace el abogado.
     */
    const buildPowerPayload = () => ({
        verification_id: formData.verification_id,
        power_city: formData.power_city,
        power_day: formData.power_day,
        power_month: formData.power_month,
        power_year: formData.power_year,
        legal_representative_full_name: formData.legal_representative_full_name || `${formData.legal_representative_first_name} ${formData.legal_representative_paternal_last_name} ${formData.legal_representative_maternal_last_name}`.trim(),
        legal_representative_rut: formData.legal_representative_rut,
        company_name: formData.company_name,
        company_rut: formData.company_rut,
        power_address: formData.power_address,
        power_municipality: formData.power_municipality,
        power_residence_city: formData.power_residence_city
    });

    /**
     * Genera el documento y lo deja listo para que el solicitante lo firme por enlace.
     * Es el camino del abogado: él no firma por el cliente.
     */
    const handleGenerateDocumentForSignature = () => {
        if (!formData.power_city || !formData.power_address || !formData.power_municipality || !formData.power_residence_city) {
            toast.error('Complete todos los campos del poder simple antes de generar el documento');
            return;
        }

        generatePowerPdf(buildPowerPayload(), {
            onSuccess: () => {
                toast.success('Documento generado. Ya puedes enviarle el enlace de firma al cliente desde la Etapa 3.');
                setCurrentStep(currentStep + 1);
            },
            onError: (error: any) => {
                toast.error(error.message || 'Error al generar el documento del poder');
            }
        });
    };

    /**
     * Abre el editor de firma PDF
     * Primero genera el PDF del poder y luego abre el editor
     */
    const handleOpenPdfSignatureEditor = () => {
        // Validar campos requeridos
        if (!formData.power_city || !formData.power_address || !formData.power_municipality || !formData.power_residence_city) {
            toast.error('Complete todos los campos del poder simple antes de firmar');
            return;
        }
        
        // Generar PDF para previsualización
        const powerData = {
            verification_id: formData.verification_id,
            power_city: formData.power_city,
            power_day: formData.power_day,
            power_month: formData.power_month,
            power_year: formData.power_year,
            legal_representative_full_name: formData.legal_representative_full_name || `${formData.legal_representative_first_name} ${formData.legal_representative_paternal_last_name} ${formData.legal_representative_maternal_last_name}`.trim(),
            legal_representative_rut: formData.legal_representative_rut,
            company_name: formData.company_name,
            company_rut: formData.company_rut,
            power_address: formData.power_address,
            power_municipality: formData.power_municipality,
            power_residence_city: formData.power_residence_city
        };
        
        console.log('Calling generatePowerPdf with data:', powerData);
        
        generatePowerPdf(powerData, {
            onSuccess: (response: any) => {
                console.log('✅ generatePowerPdf onSuccess called!', response);
                
                // Usar buildEndpoint para construir la URL correctamente (misma configuración que las demás peticiones)
                const pdfUrl = buildEndpoint(`brand-registrations/inapi-application/${formData.verification_id}/get-unsigned-pdf`);
                
                console.log('Opening PDF signature editor with URL:', pdfUrl);
                setPdfPreviewUrl(pdfUrl);
                setShowPdfSignatureEditor(true);
                toast.success('PDF generado. Ahora puedes firmarlo.');
            },
            onError: (error: any) => {
                console.error('❌ Error generating PDF:', error);
                alert('Error: ' + (error.message || 'Error desconocido'));
                toast.error(error.message || 'Error al generar el PDF del poder');
            }
        });
    };
    
    /**
     * Maneja la firma del documento PDF
     * Recibe el blob del PDF firmado y lo guarda
     */
    const handleSignDocument = async (signedPdfBlob: Blob, signatures: any[]) => {
        // Se cierra el editor al tiro: con el editor abierto se podía volver a firmar
        // mientras subía el PDF y se mandaban dos firmas
        if (fetchingSaveSignedPdf) return;
        setShowPdfSignatureEditor(false);
        try {
            // Convertir blob a base64
            const reader = new FileReader();
            reader.onload = () => {
                const base64Data = reader.result as string;
                
                saveSignedPdf({
                    verification_id: formData.verification_id,
                    signed_pdf_base64: base64Data,
                    signatures: signatures
                }, {
                    onSuccess: (response: any) => {
                        if (response.data && response.data.power_document_url) {
                            setFormData({
                                ...formData,
                                power_document_url: response.data.power_document_url,
                                signature_image_url: 'signed' // Marcar como firmado
                            });
                        }
                        setShowPdfSignatureEditor(false);
                        setPdfPreviewUrl(null);
                        toast.success('Documento firmado y guardado correctamente');
                    },
                    onError: (error: any) => {
                        toast.error(error.message || 'Error al guardar el documento firmado');
                    }
                });
            };
            reader.readAsDataURL(signedPdfBlob);
        } catch (error) {
            console.error('Error al procesar la firma:', error);
            toast.error('Error al procesar la firma del documento');
        }
    };
    
    const handleSubmit = () => {
        // Modal de confirmación personalizado
        requestConfirmation({
            title: '¿Enviar solicitud INAPI?',
            message: 'Una vez enviada, la solicitud será presentada en INAPI y cambiará su estado a "Presentado en INAPI". Esta acción no se puede deshacer.',
            buttons: {
                confirmButton: {
                    label: 'Sí, enviar',
                    onClick: () => {
                        const confirmData = {
                            verification_id: formData.verification_id
                        };
                        
                        confirmInapiApplication(confirmData, {
                            onSuccess: () => {
                                toast.success('¡Solicitud INAPI enviada exitosamente! La solicitud ha sido presentada en INAPI.');
                                
                                // Esperar 2 segundos antes de redirigir para que el usuario vea el mensaje
                                setTimeout(() => {
                                    history.push('/intranet/brand-registration');
                                }, 2000);
                            },
                            onError: (error) => {
                                toast.error(error.message || 'Error al confirmar la solicitud');
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar',
                    onClick: () => {
                        // No hacer nada, el modal se cierra automáticamente
                    }
                }
            }
        });
    };
    
    const handleNext = async () => {
        // Paso 0: Guardar datos del solicitante y crear registro INAPI inicial
        if (currentStep === 0) {
            const applicantData = {
                verification_id: formData.verification_id,
                person_type: formData.person_type,
                legal_representative_first_name: formData.legal_representative_first_name,
                legal_representative_paternal_last_name: formData.legal_representative_paternal_last_name,
                legal_representative_maternal_last_name: formData.legal_representative_maternal_last_name,
                legal_representative_rut: formData.legal_representative_rut,
                legal_representative_email: formData.legal_representative_email,
                legal_representative_phone: formData.legal_representative_phone,
                legal_representative_nationality_country: formData.legal_representative_nationality_country,
                company_name: formData.company_name,
                company_rut: formData.company_rut,
                residence_country_id: formData.residence_country_id,
                residence_country_name: formData.residence_country_name,
                residence_region_id: formData.residence_region_id,
                residence_region_name: formData.residence_region_name,
                residence_commune_id: formData.residence_commune_id,
                residence_commune_name: formData.residence_commune_name,
                residence_city: formData.residence_city,
                residence_address: formData.residence_address
            };
            
            createInapiApplication(applicantData, {
                onSuccess: () => {
                    toast.success('Datos guardados correctamente');
                    
                    // Pre-rellenar los campos del poder simple con los datos del solicitante
                    // Solo si los campos del poder están vacíos (no sobrescribir datos existentes)
                    const fullName = `${formData.legal_representative_first_name} ${formData.legal_representative_paternal_last_name} ${formData.legal_representative_maternal_last_name}`.trim();
                    
                    setFormData(prev => ({
                        ...prev,
                        // Pre-rellenar nombre completo del representante legal
                        legal_representative_full_name: prev.legal_representative_full_name || fullName,
                        // Pre-rellenar ciudad del poder con la comuna del solicitante
                        power_city: prev.power_city || prev.residence_commune_name,
                        // Pre-rellenar dirección del poder con la dirección del solicitante
                        power_address: prev.power_address || prev.residence_address,
                        // Pre-rellenar comuna del poder con la comuna del solicitante
                        power_municipality: prev.power_municipality || prev.residence_commune_name,
                        // Pre-rellenar ciudad de residencia del poder
                        power_residence_city: prev.power_residence_city || prev.residence_commune_name
                    }));
                    
                    setCurrentStep(currentStep + 1);
                },
                onError: (error) => {
                    toast.error(error.message || 'Error al guardar los datos');
                },
                onFieldError: (errors) => {
                    setErrors(errors.data);
                    toast.error('Por favor, corrige los errores en el formulario');
                }
            });
            return;
        }
        
        // Paso 1: Guardar poder y firma
        if (currentStep === 1) {
            // Validar que los campos del poder estén completos
            if (!formData.power_city || !formData.power_address || !formData.power_municipality || !formData.power_residence_city) {
                toast.error('Por favor, complete todos los campos del poder simple');
                return;
            }
            
            // Solo el solicitante firma. Si quien edita es el abogado, basta con que
            // haya dejado el documento generado para enviárselo a firmar.
            if (!isApplicant) {
                handleGenerateDocumentForSignature();
                return;
            }

            // Validar que el documento haya sido firmado digitalmente
            if (!formData.power_document_url) {
                toast.error('Por favor, genere y firme el documento de poder simple');
                return;
            }

            // Ya tenemos el documento firmado, avanzamos al siguiente paso
            toast.success('Poder simple firmado correctamente');
            setCurrentStep(currentStep + 1);
            return;
            
        }
        
        // Avanzar al siguiente paso sin llamar servicio
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };
    
    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };
    
    const renderStepContent = () => {
        switch (currentStep) {
            case 0: // Datos del solicitante
                return (
                    <div className="step-container">
                        <div className="step-header mb-4">
                            <h4 className="">
                                <i className="fas fa-user-circle me-2 text-primary"></i>
                                Datos del solicitante
                            </h4>
                            <p className="text-muted mb-0">Complete la información personal y de contacto</p>
                        </div>
                        
                        {/* Selección de tipo de persona */}
                        <div className="mb-4">
                            <h6 className="section-subtitle-simple mb-3">
                                <i className="fas fa-building me-2"></i>
                                Tipo de persona
                            </h6>
                            <OptionSelector
                                name="person_type"
                                options={[
                                    { 
                                        value: 'natural', 
                                        label: 'Persona natural', 
                                        description: 'Persona individual actuando en su propio nombre' 
                                    },
                                    { 
                                        value: 'juridica', 
                                        label: 'Persona jurídica', 
                                        description: 'Empresa o entidad legal constituida' 
                                    }
                                ]}
                                selectedValue={formData.person_type}
                                onChange={(value) => {
                                    // Si cambia a persona natural y hay datos del solicitante, pre-rellenar
                                    if (value === 'natural' && applicantData) {
                                        setFormData({ 
                                            ...formData, 
                                            person_type: value,
                                            legal_representative_first_name: formData.legal_representative_first_name || applicantData.first_name,
                                            legal_representative_paternal_last_name: formData.legal_representative_paternal_last_name || applicantData.paternal_last_name,
                                            legal_representative_maternal_last_name: formData.legal_representative_maternal_last_name || applicantData.maternal_last_name,
                                            legal_representative_email: formData.legal_representative_email || applicantData.email,
                                            legal_representative_phone: formData.legal_representative_phone || applicantData.phone
                                        });
                                    } else {
                                        setFormData({ ...formData, person_type: value });
                                    }
                                }}
                                required
                            />
                        </div>
                        
                        {/* Datos del Representante Legal / Datos Personales */}
                        <div className="mb-4">
                            <h6 className="section-subtitle-simple mb-3">
                                <i className={`fas ${formData.person_type === 'natural' ? 'fa-user' : 'fa-user-tie'} me-2`}></i>
                                {formData.person_type === 'natural' ? 'Datos personales' : 'Representante Legal'}
                            </h6>
                            <div className="row">
                                <div className="col-md-4 mb-3">
                                    <InputField
                                        label="Nombres"
                                        value={formData.legal_representative_first_name}
                                        onChange={handleInputChange('legal_representative_first_name')}
                                        onFocus={() => onFocusRemove('legal_representative_first_name')}
                                        error={fieldErrorMessage('legal_representative_first_name')}
                                        placeholder="Ingresa nombres"
                                        required
                                    />
                                </div>
                                <div className="col-md-4 mb-3">
                                    <InputField
                                        label="Apellido paterno"
                                        value={formData.legal_representative_paternal_last_name}
                                        onChange={handleInputChange('legal_representative_paternal_last_name')}
                                        onFocus={() => onFocusRemove('legal_representative_paternal_last_name')}
                                        error={fieldErrorMessage('legal_representative_paternal_last_name')}
                                        placeholder="Ingresa apellido paterno"
                                        required
                                    />
                                </div>
                                <div className="col-md-4 mb-3">
                                    <InputField
                                        label="Apellido materno"
                                        value={formData.legal_representative_maternal_last_name}
                                        onChange={handleInputChange('legal_representative_maternal_last_name')}
                                        onFocus={() => onFocusRemove('legal_representative_maternal_last_name')}
                                        error={fieldErrorMessage('legal_representative_maternal_last_name')}
                                        placeholder="Ingresa apellido materno"
                                        required
                                    />
                                </div>
                                <div className="col-md-4 mb-3">
                                    <InputField
                                        label="RUT"
                                        value={formData.legal_representative_rut}
                                        onChange={handleRutChange('legal_representative_rut')}
                                        onFocus={() => onFocusRemove('legal_representative_rut')}
                                        error={fieldErrorMessage('legal_representative_rut')}
                                        placeholder="12.345.678-9"
                                        required
                                    />
                                </div>
                                <div className="col-md-4 mb-3">
                                    <InputField
                                        type="email"
                                        label="Correo electrónico"
                                        value={formData.legal_representative_email}
                                        onChange={handleInputChange('legal_representative_email')}
                                        onFocus={() => onFocusRemove('legal_representative_email')}
                                        error={fieldErrorMessage('legal_representative_email')}
                                        placeholder="correo@ejemplo.com"
                                        required
                                    />
                                </div>
                                <div className="col-md-4 mb-3">
                                    <InputField
                                        type="tel"
                                        label="Teléfono"
                                        value={formData.legal_representative_phone}
                                        onChange={handleInputChange('legal_representative_phone')}
                                        onFocus={() => onFocusRemove('legal_representative_phone')}
                                        error={fieldErrorMessage('legal_representative_phone')}
                                        placeholder="+56 9 1234 5678"
                                        required
                                    />
                                </div>
                                <div className="col-md-4 mb-3">
                                    <InputField
                                        label="País de nacionalidad"
                                        value={formData.legal_representative_nationality_country}
                                        onChange={handleInputChange('legal_representative_nationality_country')}
                                        onFocus={() => onFocusRemove('legal_representative_nationality_country')}
                                        error={fieldErrorMessage('legal_representative_nationality_country')}
                                        placeholder="Chile"
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                        
                        {/* Datos de persona jurídica (solo si es jurídica) */}
                        {formData.person_type === 'juridica' && (
                            <div className="mb-4">
                                <h6 className="section-subtitle-simple mb-3">
                                    <i className="fas fa-building me-2"></i>
                                    Empresa
                                </h6>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <InputField
                                            label="Razón social"
                                            value={formData.company_name}
                                            onChange={handleInputChange('company_name')}
                                            onFocus={() => onFocusRemove('company_name')}
                                            error={fieldErrorMessage('company_name')}
                                            placeholder="Ingresa razón social"
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <InputField
                                            label="RUT de la empresa"
                                            value={formData.company_rut}
                                            onChange={handleRutChange('company_rut')}
                                            onFocus={() => onFocusRemove('company_rut')}
                                            error={fieldErrorMessage('company_rut')}
                                            placeholder="12.345.678-9"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Dirección */}
                        <div className="mb-4">
                            <h6 className="section-subtitle-simple mb-3">
                                <i className="fas fa-map-marker-alt me-2"></i>
                                Dirección
                            </h6>
                            <div className="row">
                                <div className={isChileSelected() ? "col-md-3 mb-3" : "col-md-6 mb-3"}>
                                    <SelectTwo
                                        label="País de residencia"
                                        inputValue={formData.residence_country_id}
                                        onChange={handleCountryChange}
                                        options={countries.map(c => ({ value: c.id, label: c.name }))}
                                        onFocus={() => onFocusRemove('residence_country_id')}
                                        placeholder="Selecciona un país"
                                        isClearable
                                    />
                                    {fieldErrorMessage('residence_country_id') && (
                                        <div className="text-danger small mt-1">
                                            {fieldErrorMessage('residence_country_id')}
                                        </div>
                                    )}
                                </div>
                                
                                {/* Mostrar Región y Comuna solo si es Chile */}
                                {isChileSelected() ? (
                                    <>
                                        <div className="col-md-3 mb-3">
                                            <SelectTwo
                                                label="Región"
                                                inputValue={formData.residence_region_id}
                                                onChange={handleRegionChange}
                                                options={regions.map(r => ({ value: r.id, label: r.name }))}
                                                onFocus={() => onFocusRemove('residence_region_id')}
                                                placeholder="Selecciona una región"
                                                isClearable
                                            />
                                            {fieldErrorMessage('residence_region_id') && (
                                                <div className="text-danger small mt-1">
                                                    {fieldErrorMessage('residence_region_id')}
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-3 mb-3">
                                            <SelectTwo
                                                label="Comuna"
                                                inputValue={formData.residence_commune_id}
                                                onChange={handleCommuneChange}
                                                options={communes.map(c => ({ value: c.id, label: c.name }))}
                                                onFocus={() => onFocusRemove('residence_commune_id')}
                                                placeholder={formData.residence_region_id ? "Selecciona una comuna" : "Primero selecciona una región"}
                                                isClearable
                                                disabled={!formData.residence_region_id}
                                            />
                                            {fieldErrorMessage('residence_commune_id') && (
                                                <div className="text-danger small mt-1">
                                                    {fieldErrorMessage('residence_commune_id')}
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-3 mb-3">
                                            <InputField
                                                label="Ciudad"
                                                value={formData.residence_city}
                                                onChange={handleResidenceCityChange}
                                                onFocus={() => onFocusRemove('residence_city')}
                                                error={fieldErrorMessage('residence_city')}
                                                placeholder="Ingresa la ciudad"
                                                required
                                            />
                                        </div>
                                    </>
                                ) : (
                                    /* Si NO es Chile, mostrar campo de ciudad manual */
                                    <div className="col-md-6 mb-3">
                                        <InputField
                                            label="Ciudad"
                                            value={formData.residence_city}
                                            onChange={handleResidenceCityChange}
                                            onFocus={() => onFocusRemove('residence_city')}
                                            error={fieldErrorMessage('residence_city')}
                                            placeholder="Ingresa la ciudad"
                                            required
                                        />
                                    </div>
                                )}
                                
                                <div className="col-md-12 mb-3">
                                    <InputField
                                        label="Dirección"
                                        value={formData.residence_address}
                                        onChange={handleInputChange('residence_address')}
                                        onFocus={() => onFocusRemove('residence_address')}
                                        error={fieldErrorMessage('residence_address')}
                                        placeholder="Calle, número, departamento"
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                );
                
            case 1: // Poder simple y firma
                return (
                    <div className="step-container">
                        <div className="step-header mb-4">
                            <h4 className="" style={{ whiteSpace: 'nowrap' }}>
                                <i className="fas fa-file-signature me-2 text-primary"></i>
                                Poder simple y firma
                            </h4>
                            <p className="text-muted mb-0">Complete los datos del poder simple y suba su firma</p>
                        </div>
                        
                        {/* Formulario de poder simple */}
                        <div className="mb-4">
                            <h6 className="section-subtitle-simple mb-3">
                                <i className="fas fa-file-signature me-2"></i>
                                Datos del poder
                            </h6>
                            <div className="power-document-section">

                            <div className="power-form-container">
                                <div className="power-form-text">
                                    En la ciudad de{' '}
                                    <InputField
                                        value={formData.power_city}
                                        onChange={handleInputChange('power_city')}
                                        placeholder="Ciudad"
                                        width="180px"
                                        required
                                    />
                                    , con fecha{' '}
                                    <InputField
                                        type="number"
                                        value={formData.power_day.toString()}
                                        onChange={handleNumberChange('power_day')}
                                        placeholder="DD"
                                        width="70px"
                                        min="1"
                                        max="31"
                                        required
                                    />
                                    {' '}de{' '}
                                    <InputField
                                        type="number"
                                        value={formData.power_month.toString()}
                                        onChange={handleNumberChange('power_month')}
                                        placeholder="MM"
                                        width="70px"
                                        min="1"
                                        max="12"
                                        required
                                    />
                                    {' '}del año{' '}
                                    <InputField
                                        type="number"
                                        value={formData.power_year.toString()}
                                        onChange={handleNumberChange('power_year')}
                                        placeholder="AAAA"
                                        width="90px"
                                        min="2000"
                                        required
                                    />
                                    , don(na){' '}
                                    <InputField
                                        value={formData.legal_representative_full_name}
                                        onChange={handleInputChange('legal_representative_full_name')}
                                        placeholder="Nombre completo"
                                        width="240px"
                                        required
                                    />
                                    , cédula de identidad número{' '}
                                    <InputField
                                        value={formData.legal_representative_rut}
                                        onChange={handleInputChange('legal_representative_rut')}
                                        placeholder="12.345.678-9"
                                        width="160px"
                                        required
                                    />
                                    {/* La representación de una empresa solo va para persona jurídica (igual que el PDF) */}
                                    {formData.person_type !== 'natural' ? (
                                        <>
                                            , en representación de la empresa{' '}
                                            <InputField
                                                value={formData.company_name}
                                                onChange={handleInputChange('company_name')}
                                                placeholder="Razón social"
                                                width="240px"
                                                required
                                            />
                                            , rol único tributario{' '}
                                            <InputField
                                                value={formData.company_rut}
                                                onChange={handleInputChange('company_rut')}
                                                placeholder="12.345.678-9"
                                                width="160px"
                                                required
                                            />
                                            {' '}en adelante &quot;la empresa&quot;, ambos con domicilio en{' '}
                                        </>
                                    ) : (
                                        <>, con domicilio en{' '}</>
                                    )}
                                    <InputField
                                        value={formData.power_address}
                                        onChange={handleInputChange('power_address')}
                                        placeholder="Dirección completa"
                                        width="240px"
                                        required
                                    />
                                    , comuna de{' '}
                                    <InputField
                                        value={formData.power_municipality}
                                        onChange={handleInputChange('power_municipality')}
                                        placeholder="Comuna"
                                        width="180px"
                                        required
                                    />
                                    {' '}y ciudad de{' '}
                                    <InputField
                                        value={formData.power_residence_city}
                                        onChange={handleInputChange('power_residence_city')}
                                        placeholder="Ciudad"
                                        width="180px"
                                        required
                                    />
                                    {' '}expone:
                                </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Sección de firma digital */}
                        <div className="signature-section mb-4">
                            <h6 className="section-subtitle-simple mb-3">
                                <i className="fas fa-signature me-2"></i>
                                Firma Digital del Documento
                            </h6>
                            
                            <div className="alert alert-info border-primary mb-3">
                                <div className="d-flex align-items-start">
                                    <i className="fas fa-file-signature me-2 mt-1 text-primary"></i>
                                    <div>
                                        <strong>Firma digital interactiva</strong>
                                        <p className="mb-0 mt-1 small text-muted">
                                            Haz clic en el botón para generar el documento de poder y firmarlo digitalmente. 
                                            Podrás dibujar tu firma, escribirla con diferentes estilos, o subir una imagen.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="d-flex flex-column align-items-center gap-3 p-4 bg-light rounded-3 border">
                                {formData.power_document_url ? (
                                    <>
                                        <div className="text-success text-center">
                                            <i className="fas fa-check-circle fa-3x mb-2"></i>
                                            <p className="mb-0 fw-bold">Documento firmado correctamente</p>
                                            <p className="small text-muted mb-0">El poder simple ha sido firmado y guardado</p>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <ButtonGeneral
                                                variant="outline"
                                                onClick={handleDownloadFinalDocument}
                                                title="Descargar PDF"
                                                icon="fa-download"
                                                size="small"
                                            />
                                            <ButtonGeneral
                                                variant="primary"
                                                onClick={() => {
                                                    // Regenerar PDF para firmar nuevamente
                                                    handleOpenPdfSignatureEditor();
                                                }}
                                                title="Firmar nuevamente"
                                                icon="fa-redo"
                                                size="small"
                                                loading={fetchingGeneratePowerPdf}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-file-pdf fa-4x text-muted"></i>
                                        <p className="text-muted mb-0 text-center">
                                            Cuando hayas completado los datos del poder, podrás generar el documento y firmarlo
                                        </p>
                                        <ButtonGeneral
                                            variant="primary"
                                            onClick={() => handleOpenPdfSignatureEditor()}
                                            title="Generar y Firmar Documento"
                                            icon="fa-signature"
                                            loading={fetchingGeneratePowerPdf}
                                            disabled={!formData.power_city || !formData.power_address || !formData.power_municipality || !formData.power_residence_city}
                                        />
                                        {(!formData.power_city || !formData.power_address || !formData.power_municipality || !formData.power_residence_city) && (
                                            <p className="text-warning small mb-0">
                                                <i className="fas fa-exclamation-triangle me-1"></i>
                                                Complete todos los campos del poder simple para habilitar la firma
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>
                            
                            {fieldErrorMessage('signature_image') && (
                                <div className="text-danger small mt-2">
                                    <i className="fas fa-exclamation-circle me-1"></i>
                                    {fieldErrorMessage('signature_image')}
                                </div>
                            )}
                        </div>
                    </div>
                );
                
            case 2: // Resumen y confirmación
                return (
                    <div className="step-container">
                        <div className="step-header mb-4">
                            <h4 className="" style={{ whiteSpace: 'nowrap' }}>
                                <i className="fas fa-clipboard-check me-2 text-primary"></i>
                                Resumen y confirmación
                            </h4>
                            <p className="text-muted mb-0">Revise todos los datos antes de enviar la solicitud INAPI</p>
                        </div>
                        
                        <div className="alert alert-info border-info mb-4">
                            <div className="d-flex align-items-start">
                                <i className="fas fa-info-circle me-2 mt-1"></i>
                                <div>
                                    <strong>Verificación final</strong>
                                    <p className="mb-0 mt-1">Por favor, revise cuidadosamente toda la información antes de enviar. Una vez enviada, la solicitud será procesada por INAPI.</p>
                                </div>
                            </div>
                        </div>
                        
                        {/* Representante Legal */}
                        <div className="mb-4">
                            <div 
                                style={{ 
                                    borderRadius: '0.75rem',
                                    border: '1px solid #e0e0e0',
                                    backgroundColor: '#ffffff',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                    overflow: 'hidden'
                                }}
                            >
                                <div 
                                    style={{ 
                                        padding: '1rem 1.5rem',
                                        backgroundColor: '#001849',
                                        color: 'white',
                                        borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
                                    }}
                                >
                                    <div className="d-flex align-items-center">
                                        <i className="fas fa-user-tie me-2" style={{ fontSize: '1.1rem', color: 'white' }}></i>
                                        <h6 className="mb-0" style={{ fontSize: '1.05rem', fontWeight: '600', color: 'white' }}>
                                            Representante Legal
                                        </h6>
                                    </div>
                                </div>
                                
                                <div style={{ padding: '1.5rem' }}>
                            <div className="row g-3">
                                        <div className="col-md-12">
                                            <div style={{ marginBottom: '0.75rem' }}>
                                                <div 
                                                    style={{ 
                                                        fontSize: '0.75rem', 
                                                        fontWeight: '600', 
                                                        color: '#6c757d',
                                                        marginBottom: '0.5rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Nombre Completo
                                                </div>
                                                <div 
                                                    style={{ 
                                                        fontSize: '1rem', 
                                                        color: '#001849',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {formData.legal_representative_first_name} {formData.legal_representative_paternal_last_name} {formData.legal_representative_maternal_last_name}
                                                </div>
                                            </div>
                                        </div>
                                <div className="col-md-6">
                                            <div style={{ marginBottom: '0.75rem' }}>
                                                <div 
                                                    style={{ 
                                                        fontSize: '0.75rem', 
                                                        fontWeight: '600', 
                                                        color: '#6c757d',
                                                        marginBottom: '0.5rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    RUT
                                        </div>
                                                <div 
                                                    style={{ 
                                                        fontSize: '1rem', 
                                                        color: '#001849',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {formData.legal_representative_rut || '-'}
                                            </div>
                                            </div>
                                            </div>
                                        <div className="col-md-6">
                                            <div style={{ marginBottom: '0.75rem' }}>
                                                <div 
                                                    style={{ 
                                                        fontSize: '0.75rem', 
                                                        fontWeight: '600', 
                                                        color: '#6c757d',
                                                        marginBottom: '0.5rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Nacionalidad
                                        </div>
                                                <div 
                                                    style={{ 
                                                        fontSize: '1rem', 
                                                        color: '#001849',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {formData.legal_representative_nationality_country || '-'}
                                    </div>
                                </div>
                                        </div>
                                <div className="col-md-6">
                                            <div style={{ marginBottom: '0.75rem' }}>
                                                <div 
                                                    style={{ 
                                                        fontSize: '0.75rem', 
                                                        fontWeight: '600', 
                                                        color: '#6c757d',
                                                        marginBottom: '0.5rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Correo Electrónico
                                        </div>
                                                <div 
                                                    style={{ 
                                                        fontSize: '1rem', 
                                                        color: '#001849',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {formData.legal_representative_email || '-'}
                                            </div>
                                            </div>
                                            </div>
                                        <div className="col-md-6">
                                            <div style={{ marginBottom: '0.75rem' }}>
                                                <div 
                                                    style={{ 
                                                        fontSize: '0.75rem', 
                                                        fontWeight: '600', 
                                                        color: '#6c757d',
                                                        marginBottom: '0.5rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Teléfono
                                                </div>
                                                <div 
                                                    style={{ 
                                                        fontSize: '1rem', 
                                                        color: '#001849',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {formData.legal_representative_phone || '-'}
                                                </div>
                                            </div>
                                        </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                        {/* Empresa (solo si es jurídica) */}
                            {formData.person_type === 'juridica' && (
                            <div className="mb-4">
                                <div 
                                    style={{ 
                                        borderRadius: '0.75rem',
                                        border: '1px solid #e0e0e0',
                                        backgroundColor: '#ffffff',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <div 
                                        style={{ 
                                            padding: '1rem 1.5rem',
                                            backgroundColor: '#001849',
                                            color: 'white',
                                            borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
                                        }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <i className="fas fa-building me-2" style={{ fontSize: '1.1rem', color: 'white' }}></i>
                                            <h6 className="mb-0" style={{ fontSize: '1.05rem', fontWeight: '600', color: 'white' }}>
                                                Empresa
                                            </h6>
                                            </div>
                                    </div>
                                    
                                    <div style={{ padding: '1.5rem' }}>
                                        <div className="row g-3">
                                                    <div className="col-md-8">
                                                <div style={{ marginBottom: '0.75rem' }}>
                                                    <div 
                                                        style={{ 
                                                            fontSize: '0.75rem', 
                                                            fontWeight: '600', 
                                                            color: '#6c757d',
                                                            marginBottom: '0.5rem',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px'
                                                        }}
                                                    >
                                                        Razón Social
                                                    </div>
                                                    <div 
                                                        style={{ 
                                                            fontSize: '1rem', 
                                                            color: '#001849',
                                                            fontWeight: '500'
                                                        }}
                                                    >
                                                        {formData.company_name || '-'}
                                                    </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                <div style={{ marginBottom: '0.75rem' }}>
                                                    <div 
                                                        style={{ 
                                                            fontSize: '0.75rem', 
                                                            fontWeight: '600', 
                                                            color: '#6c757d',
                                                            marginBottom: '0.5rem',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px'
                                                        }}
                                                    >
                                                        RUT Empresa
                                                    </div>
                                                    <div 
                                                        style={{ 
                                                            fontSize: '1rem', 
                                                            color: '#001849',
                                                            fontWeight: '500'
                                                        }}
                                                    >
                                                        {formData.company_rut || '-'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                        {/* Dirección */}
                        <div className="mb-4">
                            <div 
                                style={{ 
                                    borderRadius: '0.75rem',
                                    border: '1px solid #e0e0e0',
                                    backgroundColor: '#ffffff',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                    overflow: 'hidden'
                                }}
                            >
                                <div 
                                    style={{ 
                                        padding: '1rem 1.5rem',
                                        backgroundColor: '#001849',
                                        color: 'white',
                                        borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
                                    }}
                                >
                                    <div className="d-flex align-items-center">
                                        <i className="fas fa-map-marker-alt me-2" style={{ fontSize: '1.1rem', color: 'white' }}></i>
                                        <h6 className="mb-0" style={{ fontSize: '1.05rem', fontWeight: '600', color: 'white' }}>
                                            Dirección
                                        </h6>
                                        </div>
                                </div>
                                
                                <div style={{ padding: '1.5rem' }}>
                                    <div className="row g-3">
                                        <div className="col-md-12">
                                            <div style={{ marginBottom: '0.75rem' }}>
                                                <div 
                                                    style={{ 
                                                        fontSize: '0.75rem', 
                                                        fontWeight: '600', 
                                                        color: '#6c757d',
                                                        marginBottom: '0.5rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Dirección Completa
                                                </div>
                                                <div 
                                                    style={{ 
                                                        fontSize: '1rem', 
                                                        color: '#001849',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {formData.residence_address || '-'}
                                                </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-3">
                                            <div style={{ marginBottom: '0.75rem' }}>
                                                <div 
                                                    style={{ 
                                                        fontSize: '0.75rem', 
                                                        fontWeight: '600', 
                                                        color: '#6c757d',
                                                        marginBottom: '0.5rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Región
                                                    </div>
                                                <div 
                                                    style={{ 
                                                        fontSize: '1rem', 
                                                        color: '#001849',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {formData.residence_region_name || '-'}
                                                </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-3">
                                            <div style={{ marginBottom: '0.75rem' }}>
                                                <div 
                                                    style={{ 
                                                        fontSize: '0.75rem', 
                                                        fontWeight: '600', 
                                                        color: '#6c757d',
                                                        marginBottom: '0.5rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Comuna
                                                    </div>
                                                <div 
                                                    style={{ 
                                                        fontSize: '1rem', 
                                                        color: '#001849',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {formData.residence_commune_name || '-'}
                                                </div>
                                                    </div>
                                                </div>
                                        <div className="col-md-3">
                                            <div style={{ marginBottom: '0.75rem' }}>
                                                <div 
                                                    style={{ 
                                                        fontSize: '0.75rem', 
                                                        fontWeight: '600', 
                                                        color: '#6c757d',
                                                        marginBottom: '0.5rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    País
                                                    </div>
                                                <div 
                                                    style={{ 
                                                        fontSize: '1rem', 
                                                        color: '#001849',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {formData.residence_country_name || '-'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Datos del Poder */}
                        <div className="mb-4">
                            <div 
                                style={{ 
                                    borderRadius: '0.75rem',
                                    border: '1px solid #e0e0e0',
                                    backgroundColor: '#ffffff',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                    overflow: 'hidden'
                                }}
                            >
                                <div 
                                    style={{ 
                                        padding: '1rem 1.5rem',
                                        backgroundColor: '#001849',
                                        color: 'white',
                                        borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
                                    }}
                                >
                                    <div className="d-flex align-items-center">
                                        <i className="fas fa-file-signature me-2" style={{ fontSize: '1.1rem', color: 'white' }}></i>
                                        <h6 className="mb-0" style={{ fontSize: '1.05rem', fontWeight: '600', color: 'white' }}>
                                            Datos del Poder
                                        </h6>
                                    </div>
                                </div>
                                
                                <div style={{ padding: '1.5rem' }}>
                            <div className="row g-3">
                                        <div className="col-md-4">
                                            <div style={{ marginBottom: '0.75rem' }}>
                                                <div 
                                                    style={{ 
                                                        fontSize: '0.75rem', 
                                                        fontWeight: '600', 
                                                        color: '#6c757d',
                                                        marginBottom: '0.5rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Ciudad del Poder
                                        </div>
                                                <div 
                                                    style={{ 
                                                        fontSize: '1rem', 
                                                        color: '#001849',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {formData.power_city || '-'}
                                                    </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                            <div style={{ marginBottom: '0.75rem' }}>
                                                <div 
                                                    style={{ 
                                                        fontSize: '0.75rem', 
                                                        fontWeight: '600', 
                                                        color: '#6c757d',
                                                        marginBottom: '0.5rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Fecha del Poder
                                                </div>
                                                <div 
                                                    style={{ 
                                                        fontSize: '1rem', 
                                                        color: '#001849',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                            {formData.power_day}/{formData.power_month}/{formData.power_year}
                                                </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                            <div style={{ marginBottom: '0.75rem' }}>
                                                <div 
                                                    style={{ 
                                                        fontSize: '0.75rem', 
                                                        fontWeight: '600', 
                                                        color: '#6c757d',
                                                        marginBottom: '0.5rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Representante Legal Completo
                                                </div>
                                                <div 
                                                    style={{ 
                                                        fontSize: '1rem', 
                                                        color: '#001849',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {formData.legal_representative_full_name || '-'}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-8">
                                            <div style={{ marginBottom: '0.75rem' }}>
                                                <div 
                                                    style={{ 
                                                        fontSize: '0.75rem', 
                                                        fontWeight: '600', 
                                                        color: '#6c757d',
                                                        marginBottom: '0.5rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Domicilio del Poder
                                                </div>
                                                <div 
                                                    style={{ 
                                                        fontSize: '1rem', 
                                                        color: '#001849',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {formData.power_address || '-'}
                                                </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                            <div style={{ marginBottom: '0.75rem' }}>
                                                <div 
                                                    style={{ 
                                                        fontSize: '0.75rem', 
                                                        fontWeight: '600', 
                                                        color: '#6c757d',
                                                        marginBottom: '0.5rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Comuna
                                                    </div>
                                                <div 
                                                    style={{ 
                                                        fontSize: '1rem', 
                                                        color: '#001849',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {formData.power_municipality || '-'}
                                                </div>
                                                    </div>
                                                </div>
                                        <div className="col-md-12">
                                            <div style={{ marginBottom: '0.75rem' }}>
                                                <div 
                                                    style={{ 
                                                        fontSize: '0.75rem', 
                                                        fontWeight: '600', 
                                                        color: '#6c757d',
                                                        marginBottom: '0.5rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Ciudad Residencia
                                            </div>
                                                <div 
                                                    style={{ 
                                                        fontSize: '1rem', 
                                                        color: '#001849',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {formData.power_residence_city || '-'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                                        </div>
                        </div>
                        
                        {/* Firma */}
                        <div className="mb-4">
                            <div 
                                style={{ 
                                    borderRadius: '0.75rem',
                                    border: '1px solid #e0e0e0',
                                    backgroundColor: '#ffffff',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                    overflow: 'hidden'
                                }}
                            >
                                <div 
                                    style={{ 
                                        padding: '1rem 1.5rem',
                                        backgroundColor: '#001849',
                                        color: 'white',
                                        borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
                                    }}
                                >
                                    <div className="d-flex align-items-center">
                                        <i className="fas fa-pen-nib me-2" style={{ fontSize: '1.1rem', color: 'white' }}></i>
                                        <h6 className="mb-0" style={{ fontSize: '1.05rem', fontWeight: '600', color: 'white' }}>
                                            Firma
                                        </h6>
                                    </div>
                                </div>
                                
                                <div style={{ padding: '1.5rem' }}>
                                    <div className="text-center">
                                        {formData.signature_image_url ? (
                                        <div>
                                                <img 
                                                    src={formData.signature_image_url} 
                                                    alt="Firma" 
                                                    style={{ 
                                                        maxWidth: '300px', 
                                                        maxHeight: '150px',
                                                        border: '2px solid #e0e0e0',
                                                        borderRadius: '0.5rem',
                                                        padding: '1rem',
                                                        backgroundColor: '#f8f9fa'
                                                    }} 
                                                />
                                                <p className="text-muted mt-3 mb-0 small">
                                                    <i className="fas fa-check-circle text-success me-1"></i>
                                                    Firma cargada correctamente
                                            </p>
                                        </div>
                                        ) : (
                                            <div className="text-muted py-4">
                                                <i className="fas fa-exclamation-circle text-warning me-2"></i>
                                                No se ha cargado firma
                                            </div>
                                        )}
                                    </div>
                                    </div>
                                </div>
                            </div>
                            
                        {/* Botón para descargar preview del documento */}
                        {formData.power_document_url && (
                            <div className="mb-4">
                                <div 
                                    style={{ 
                                        borderRadius: '0.75rem',
                                        border: '1px solid #e0e0e0',
                                        backgroundColor: '#f8f9fa',
                                        padding: '1.5rem',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                                    }}
                                >
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-start">
                                                <i className="fas fa-file-pdf text-danger me-3 mt-1" style={{ fontSize: '2rem' }}></i>
                                                <div>
                                                <h6 className="mb-1" style={{ color: '#001849', fontWeight: '600' }}>Poder Simple Firmado</h6>
                                                <p className="mb-0 small text-muted">Documento PDF con firma digital</p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={handleDownloadFinalDocument}
                                                disabled={loading}
                                                style={{ backgroundColor: '#001849', borderColor: '#001849' }}
                                            >
                                                {loading ? (
                                                    <>
                                                        <i className="fas fa-spinner fa-spin me-2"></i>
                                                        Descargando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-download me-2"></i>
                                                        Descargar PDF firmado
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                    </div>
                );
                
            default:
                return null;
        }
    };
    
    return (
        <div className="container-fluid">
            <PageHeader
                title="Solicitud INAPI"
                description="Complete la información para la solicitud de registro de marca"
                breadcrumbs={[
                    { label: 'Inicio', path: '/intranet' },
                    { label: 'Registro de Marca', path: '/intranet/brand-registration' },
                    { label: 'Solicitud INAPI', isCurrent: true }
                ]}
            />
            
            <div className="row">
                <div className="col-12">
                    <DefaultCard>
                        {/* Indicador de pasos */}
                        <div className="mb-4">
                            <h6 className="text-muted mb-2">Paso {currentStep + 1} de {steps.length}</h6>
                            <div className="progress mb-2">
                                <div 
                                    className="progress-bar" 
                                    role="progressbar" 
                                    style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                                />
                            </div>
                            <small className="text-muted">{steps[currentStep]}</small>
                        </div>
                        
                        {/* Contenido del paso actual */}
                        {renderStepContent()}
                        
                        {/* Botones de navegación */}
                        <div className="mt-4 d-flex justify-content-between">
                            <ButtonGeneral
                                type="button"
                                variant="secondary"
                                size="medium"
                                onClick={currentStep === 0 ? () => history.goBack() : handlePrevious}
                                title={currentStep === 0 ? 'Cancelar' : 'Anterior'}
                                disabled={loading}
                            />
                            
                            <ButtonGeneral
                                type="button"
                                variant="primary"
                                size="medium"
                                onClick={currentStep === steps.length - 1 ? handleSubmit : handleNext}
                                title={currentStep === steps.length - 1 ? 'Enviar Solicitud' : 'Siguiente'}
                                loading={loading || fetchingCreateApplication || fetchingConfirmApplication}
                                disabled={loading || fetchingCreateApplication || fetchingConfirmApplication}
                            />
                        </div>
                    </DefaultCard>
                </div>
            </div>
            
            {/* Estilos mejorados */}
            <style>{`
                /* Contenedor de pasos */
                .step-container {
                    padding: 0.5rem 0;
                }
                
                /* Header de paso */
                .step-header {
                    border-bottom: 2px solid #f0f0f0;
                    padding-bottom: 1rem;
                }
                
                .step-title {
                    font-size: 1.5rem;
                    font-weight: 600;
                    color: #2c3e50;
                    margin-bottom: 0.5rem;
                }
                
                /* Secciones del formulario */
                .form-section {
                    background: #ffffff;
                    border-radius: 8px;
                    padding: 1.5rem;
                    border: 1px solid #e9ecef;
                }
                
                .section-subtitle {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #495057;
                    margin-bottom: 1rem;
                    display: flex;
                    align-items: center;
                }
                
                .section-subtitle-simple {
                    font-size: 1.05rem;
                    font-weight: 600;
                    color: #2c3e50;
                    margin-bottom: 1rem;
                    display: flex;
                    align-items: center;
                    padding-bottom: 0.5rem;
                }
                
                /* Formulario de poder */
                .power-document-section {
                    margin-bottom: 2rem;
                }
                
                .power-form-container {
                    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
                    border: 2px solid #e9ecef;
                    border-radius: 12px;
                    padding: 2rem;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                }
                
                .power-form-text {
                    line-height: 4.5;
                    color: #2c3e50;
                    font-size: 1.05rem;
                    font-weight: 400;
                    text-align: justify;
                }
                
                .power-form-text .input-field-container {
                    display: inline-block;
                    vertical-align: middle;
                    margin: 0 0.25rem;
                }
                
                .power-form-text .input-field {
                    font-size: 1rem;
                    padding: 0.5rem 0.75rem;
                    height: 42px;
                }
                
                @media (max-width: 1400px) {
                    .power-form-text {
                        font-size: 0.98rem;
                        line-height: 4.2;
                    }
                    
                    .power-form-text .input-field-container {
                        margin: 0 0.2rem;
                    }
                    
                    .power-form-text .input-field {
                        font-size: 0.92rem;
                        padding: 0.45rem 0.65rem;
                        height: 40px;
                    }
                }
                
                @media (max-width: 1200px) {
                    .power-form-text {
                        font-size: 0.92rem;
                        line-height: 3.8;
                    }
                    
                    .power-form-text .input-field-container {
                        margin: 0 0.18rem;
                    }
                    
                    .power-form-text .input-field {
                        font-size: 0.88rem;
                        padding: 0.4rem 0.6rem;
                        height: 38px;
                    }
                }
                
                @media (max-width: 992px) {
                    .power-form-container {
                        padding: 1.5rem;
                    }
                    
                    .power-form-text {
                        font-size: 0.88rem;
                        line-height: 3.5;
                    }
                    
                    .power-form-text .input-field-container {
                        margin: 0 0.15rem;
                    }
                    
                    .power-form-text .input-field {
                        font-size: 0.82rem;
                        padding: 0.35rem 0.55rem;
                        height: 36px;
                    }
                }
                
                /* Sección de firma */
                .signature-section {
                    background: #f8f9fa;
                    border-radius: 12px;
                    padding: 1.5rem;
                    border: 2px dashed #dee2e6;
                }
                
                .signature-upload-container {
                    margin-top: 1rem;
                }
                
                /* Resumen - Tarjetas de datos */
                .summary-section {
                    margin-bottom: 2rem;
                }
                
                .summary-section-title {
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #2c3e50;
                    margin-bottom: 1rem;
                    padding-bottom: 0.75rem;
                    border-bottom: 2px solid #5B73E8;
                    display: flex;
                    align-items: center;
                }
                
                .summary-card {
                    background: #ffffff;
                    border: 1px solid #e9ecef;
                    border-radius: 10px;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                    transition: all 0.3s ease;
                    height: 100%;
                }
                
                .summary-card:hover {
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    transform: translateY(-2px);
                }
                
                .summary-card-header {
                    background: linear-gradient(135deg, #5B73E8 0%, #7B8FEB 100%);
                    color: white;
                    padding: 1rem 1.25rem;
                    font-size: 1rem;
                    display: flex;
                    align-items: center;
                }
                
                .summary-card-body {
                    padding: 1.25rem;
                }
                
                .summary-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    padding: 0.75rem 0;
                    border-bottom: 1px solid #f0f0f0;
                }
                
                .summary-item:last-child {
                    border-bottom: none;
                }
                
                .summary-label {
                    font-weight: 600;
                    color: #6c757d;
                    font-size: 0.9rem;
                    min-width: 140px;
                }
                
                .summary-value {
                    color: #2c3e50;
                    font-weight: 500;
                    text-align: right;
                    flex: 1;
                }
                
                /* Tarjetas de estado de documentos */
                .document-status-card {
                    background: #ffffff;
                    border: 2px solid #e9ecef;
                    border-radius: 10px;
                    padding: 1.25rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    transition: all 0.3s ease;
                }
                
                .document-status-card.success {
                    border-color: #34C38F;
                    background: linear-gradient(135deg, #f0fdf8 0%, #ffffff 100%);
                }
                
                .document-status-card.success i {
                    color: #34C38F;
                    font-size: 1.5rem;
                }
                
                .document-status-card.pending {
                    border-color: #F1B44C;
                    background: linear-gradient(135deg, #fffef0 0%, #ffffff 100%);
                }
                
                .document-status-card.pending i {
                    color: #F1B44C;
                    font-size: 1.5rem;
                }
                
                /* Tarjeta de descarga final */
                .download-final-card {
                    background: linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%);
                    border: 2px solid #5B73E8;
                    border-radius: 12px;
                    padding: 1.5rem;
                    box-shadow: 0 4px 12px rgba(91, 115, 232, 0.15);
                }
                
                .download-final-card h6 {
                    color: #2c3e50;
                    font-weight: 600;
                    margin: 0;
                }
                
                /* Alertas mejoradas */
                .alert {
                    border-radius: 10px;
                    border-width: 1px;
                }
                
                .alert-info {
                    background-color: #f0f9ff;
                    border-color: #b3d9f2;
                    color: #0c5a8a;
                }
                
                .alert-success {
                    background-color: #f0fdf8;
                    border-color: #b3e8d1;
                    color: #155724;
                }
                
                .alert-light {
                    background-color: #fafbfc;
                    border-color: #e1e4e8;
                }
                
                /* Barra de progreso */
                .progress {
                    height: 8px;
                    border-radius: 10px;
                    background-color: #e9ecef;
                }
                
                .progress-bar {
                    background: linear-gradient(90deg, #5B73E8 0%, #7B8FEB 100%);
                    border-radius: 10px;
                    transition: width 0.6s ease;
                }
                
                /* Botones mejorados */
                .btn {
                    border-radius: 8px;
                    padding: 0.625rem 1.5rem;
                    font-weight: 500;
                    transition: all 0.3s ease;
                }
                
                .btn-primary {
                    background: linear-gradient(135deg, #5B73E8 0%, #7B8FEB 100%);
                    border: none;
                    box-shadow: 0 2px 6px rgba(91, 115, 232, 0.3);
                }
                
                .btn-primary:hover:not(:disabled) {
                    background: linear-gradient(135deg, #4a62d7 0%, #6a7eda 100%);
                    box-shadow: 0 4px 10px rgba(91, 115, 232, 0.4);
                    transform: translateY(-1px);
                }
                
                .btn-success {
                    background: linear-gradient(135deg, #34C38F 0%, #4CD3A6 100%);
                    border: none;
                    box-shadow: 0 2px 6px rgba(52, 195, 143, 0.3);
                }
                
                .btn-success:hover:not(:disabled) {
                    background: linear-gradient(135deg, #2ab27e 0%, #3bc295 100%);
                    box-shadow: 0 4px 10px rgba(52, 195, 143, 0.4);
                    transform: translateY(-1px);
                }
                
                .btn-secondary {
                    background: #6c757d;
                    border: none;
                }
                
                .btn-secondary:hover:not(:disabled) {
                    background: #5a6268;
                    transform: translateY(-1px);
                }
                
                /* Responsivo */
                @media (max-width: 768px) {
                    .step-title {
                        font-size: 1.15rem;
                    }
                    
                    .form-section {
                        padding: 1rem;
                    }
                    
                    .power-form-container {
                        padding: 1rem;
                    }
                    
                    .power-form-text {
                        font-size: 0.82rem;
                        line-height: 3.3;
                    }
                    
                    .power-form-text .input-field-container {
                        margin: 0 0.12rem;
                    }
                    
                    .power-form-text .input-field {
                        font-size: 0.78rem;
                        padding: 0.3rem 0.5rem;
                        height: 34px;
                    }
                    
                    .summary-card-header {
                        font-size: 0.9rem;
                        padding: 0.875rem 1rem;
                    }
                    
                    .summary-card-body {
                        padding: 1rem;
                    }
                    
                    .summary-item {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 0.25rem;
                    }
                    
                    .summary-value {
                        text-align: left;
                    }
                    
                    .download-final-card .d-flex {
                        flex-direction: column !important;
                        gap: 1rem;
                    }
                    
                    .download-final-card .btn {
                        width: 100%;
                    }
                }
            `}</style>
            
            {/* Editor de Firma PDF */}
            <PDFSignatureEditor
                pdfUrl={pdfPreviewUrl || ''}
                documentTitle="Poder Simple INAPI"
                userName={formData.legal_representative_full_name || `${formData.legal_representative_first_name} ${formData.legal_representative_paternal_last_name}`.trim()}
                onSign={handleSignDocument}
                onCancel={() => {
                    setShowPdfSignatureEditor(false);
                    setPdfPreviewUrl(null);
                }}
                isVisible={showPdfSignatureEditor}
            />
        </div>
    );
};

export default InapiApplication;

